import React, { useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Building, Trophy, Zap, ArrowRight, Users } from "lucide-react";
import backgroundImage from "../assets/program-background.jpg";

const Programs = () => {
  const [activeTab, setActiveTab] = useState("mentorship");

  const mentorshipPrograms = [
    {
      title: "One-on-One Mentorship",
      description:
        "Personalized mentorship tailored to your specific career and personal development goals. Engage in regular sessions with dedicated mentors who help you map out a clear path for growth.",
      icon: <Users size={24} className="text-amber-500" />,
      featured: true,
    },
    {
      title: "Executive Mentorship",
      description:
        "Senior professionals mentor mid-level professionals aiming for leadership roles. Gain insights from industry veterans to accelerate your career advancement.",
      icon: <Building size={24} className="text-amber-500" />,
    },
    {
      title: "Career Acceleration Program",
      description:
        "Helps young professionals navigate career challenges and develop critical skills. This program focuses on overcoming obstacles and fast-tracking your career progression.",
      icon: <Zap size={24} className="text-amber-500" />,
    },
    {
      title: "Entrepreneurship Mentorship",
      description:
        "Designed for aspiring entrepreneurs, this program covers business strategy, funding, and scaling. Learn the essential skills to launch and sustain a successful venture.",
      icon: <Trophy size={24} className="text-amber-500" />,
    },
  ];

  const talentManagementPrograms = [
    {
      title: "High-Potential Talent Program",
      description:
        "Identifies and grooms individuals with high leadership potential. This program focuses on strategic development and mentoring for future leaders.",
      icon: <Zap size={24} className="text-amber-500" />,
      featured: true,
    },
    {
      title: "Corporate Talent Pipeline Program",
      description:
        "Works with organizations to train and develop future leaders internally. Learn through structured programs that align with corporate goals and foster sustainable growth.",
      icon: <Building size={24} className="text-amber-500" />,
    },
    {
      title: "Creative & Digital Talent Development",
      description:
        "Focuses on digital marketing, branding, and media skills for creatives. Enhance your digital toolkit and creative strategies to stand out in a competitive market.",
      icon: <GraduationCap size={24} className="text-amber-500" />,
    },
    {
      title: "Internship & Apprenticeship Placement Program",
      description:
        "Provides hands-on experience for young professionals entering the workforce. Gain practical skills through placements that bridge academic knowledge with real-world experience.",
      icon: <Users size={24} className="text-amber-500" />,
    },
    {
      title: "Soft Skills & Personal Branding Program",
      description:
        "Enhances communication, emotional intelligence, and networking skills. Build a strong personal brand and develop the soft skills essential for career success.",
      icon: <Trophy size={24} className="text-amber-500" />,
    },
  ];

  const leadershipDevelopmentPrograms = [
    {
      title: "Future Leaders Program",
      description:
        "Prepares young professionals for leadership roles with strategic thinking and decision-making training. Cultivate a mindset geared toward innovation and leadership.",
      icon: <Zap size={24} className="text-amber-500" />,
      featured: true,
    },
    {
      title: "Boardroom Readiness Program",
      description:
        "Trains professionals for board-level leadership and corporate governance. Learn the intricacies of strategic oversight and effective decision-making at the highest levels.",
      icon: <Building size={24} className="text-amber-500" />,
    },
    {
      title: "Leadership in a Digital Age",
      description:
        "Equips leaders with digital transformation strategies for modern businesses. Adapt to the evolving digital landscape with innovative approaches to leadership.",
      icon: <GraduationCap size={24} className="text-amber-500" />,
    },
    {
      title: "Diversity & Inclusive Leadership Program",
      description:
        "Develops leaders who drive diversity, equity, and inclusion. Embrace inclusive practices and foster a work environment where all voices are heard.",
      icon: <Users size={24} className="text-amber-500" />,
    },
    {
      title: "Resilient Leadership Program",
      description:
        "Focuses on crisis management, adaptability, and leading through uncertainty. Build resilience and learn to thrive in challenging and rapidly changing environments.",
      icon: <Trophy size={24} className="text-amber-500" />,
    },
  ];

  const CategoryIcon = ({ category }) => {
    const iconProps = { size: 48, className: "text-white" };
    
    switch (category) {
      case "mentorship":
        return <GraduationCap {...iconProps} />;
      case "talent":
        return <Building {...iconProps} />;
      case "leadership":
        return <Trophy {...iconProps} />;
      default:
        return null;
    }
  };

  const programsByCategory = {
    mentorship: mentorshipPrograms,
    talent: talentManagementPrograms,
    leadership: leadershipDevelopmentPrograms,
  };

  const categoryTitles = {
    mentorship: "Mentorship Programs",
    talent: "Talent Management Programs",
    leadership: "Leadership Development Programs",
  };

  const categoryDescriptions = {
    mentorship: "Our mentorship initiative connects aspiring professionals with experienced mentors who provide guidance, career coaching, and personal development strategies. Through structured one-on-one mentorship, group sessions, and peer support, participants gain valuable insights, industry expertise, and personal growth opportunities.",
    talent: "We identify and nurture high-potential individuals by equipping them with the skills needed to excel in their fields. Through specialized training, performance assessments, and career acceleration programs, participants are prepared for long-term success in their industries.",
    leadership: "Our leadership program is designed for individuals looking to enhance their leadership capabilities and make a lasting impact in their communities and organizations. We provide intensive leadership training, executive coaching, and real-world challenges that cultivate decision-making, innovation, and strategic thinking.",
  };

  const ProgramCard = ({ program }) => (
    <div className="bg-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-2xl flex flex-col h-full relative overflow-hidden transform hover:scale-105">
      <div className="absolute top-0 right-0 w-32 h-32  rounded-full -mr-16 -mt-16 opacity-20"></div>
      
      {/* Card content wrapper to ensure full height */}
      <div className="flex flex-col h-full p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-3 bg-amber-100 rounded-lg">
            {program.icon}
          </div>
          <div>
            <h3 className="text-2xl font-bold text-green-700 mb-3 group-hover:text-green-600 transition-colors">
              {program.title}
            </h3>
          </div>
        </div>
        
        {/* Description in the middle with flex-grow to push the button to the bottom */}
        <div className="text-gray-700 leading-relaxed mt-4 flex-grow">
          {program.description}
        </div>
      
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 lg:px-6 py-16">
      {/* Hero Section - Maintained structure but enhanced visually */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden text-white py-20 md:py-28 min-h-[50vh] md:min-h-[60vh] flex items-center justify-center text-center rounded-3xl"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Animated gradient overlay with golden accents */}
        <motion.div 
          initial={{ opacity: 0.7 }}
          animate={{ opacity: 0.9 }}
          transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
          className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-800"
        ></motion.div>

        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="z-10 px-4 overflow-visible"
        >
          <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-lg">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-amber-100 to-amber-200">
              Our Programs
            </span>
          </h1>
          <p className="mt-4 text-xl md:text-2xl max-w-2xl mx-auto text-green-50 drop-shadow">
            Discover a range of programs designed to empower, develop, and transform your career and leadership potential.
          </p>

          {/* Added CTA button with gold accent */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mt-8 px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-white font-bold rounded-full hover:from-amber-500 hover:to-amber-600 transition-colors shadow-lg"
          >
            Find Your Program
          </motion.button>
        </motion.div>

        {/* Enhanced abstract background elements with golden yellow */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            animate={{ 
              x: [0, 30, 0],
              y: [0, -30, 0],
            }} 
            transition={{ 
              duration: 8,
              repeat: Infinity,
              repeatType: "reverse" 
            }}
            className="absolute -top-1/4 -right-1/4 w-96 h-96 bg-amber-500 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
          ></motion.div>
          <motion.div 
            animate={{ 
              x: [0, -30, 0],
              y: [0, 30, 0],
            }} 
            transition={{ 
              duration: 10,
              repeat: Infinity,
              repeatType: "reverse" 
            }}
            className="absolute -bottom-1/4 -left-1/4 w-96 h-96 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
          ></motion.div>
        </div>
      </motion.section>

      {/* Program Navigation Tabs - New interactive element with gold accents */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="flex flex-wrap justify-center gap-4 mt-16 mb-12"
      >
        {Object.keys(programsByCategory).map((category) => (
          <motion.button
            key={category}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(category)}
            className={`px-6 py-4 rounded-full flex items-center gap-2 text-lg font-semibold transition-all ${
              activeTab === category
                ? "bg-gradient-to-r from-green-600 to-green-800 text-white shadow-lg"
                : "bg-gray-100 text-gray-700 hover:bg-amber-50"
            }`}
          >
            <CategoryIcon category={category} />
            <span>{categoryTitles[category]}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* Dynamic Program Section with gold accents */}
      <motion.section
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mt-12 relative px-4 sm:px-6 lg:px-12"
      >
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          {/* Icon with gold gradient background */}
          <div className="inline-flex justify-center items-center p-4 bg-gradient-to-r from-green-600 to-green-800 rounded-full mb-6 shadow-lg">
            <CategoryIcon category={activeTab} />
          </div>

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-700 mb-4">
            {categoryTitles[activeTab]}
          </h2>

          {/* Underline with gold gradient */}
          <div className="w-24 h-1 bg-amber-300 mx-auto mb-6"></div>

          {/* Description */}
          <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            {categoryDescriptions[activeTab]}
          </p>
        </motion.div>

        {/* Grid Layout with Equal Height Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {programsByCategory[activeTab].map((program, index) => (
            <motion.div
              key={program.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="w-full h-full flex"
            >
              <div className="flex-1 flex flex-col w-full">
                <ProgramCard program={program} />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* How to Apply Section - Enhanced with gold accents */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="mt-24 bg-gradient-to-r from-green-600 to-green-800 p-12 rounded-xl shadow-lg text-white"
      >
        <h2 className="text-4xl font-bold text-center mb-8">How to Apply</h2>
        <div className="w-24 h-1 bg-amber-300 mx-auto mb-12"></div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {[
            {
              step: 1,
              title: "Visit Membership",
              description: "Explore our membership options and benefits",
              icon: <Users size={32} className="text-white" />
            },
            {
              step: 2,
              title: "Select Your Tier",
              description: "Choose the membership tier that suits your goals",
              icon: <Trophy size={32} className="text-white" />
            },
            {
              step: 3,
              title: "Complete Registration",
              description: "Fill out your profile and secure your spot",
              icon: <ArrowRight size={32} className="text-white" />
            }
          ].map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.2 }}
              className="flex flex-col items-center text-center"
            >
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4">
                {step.icon}
              </div>
              <div className="text-2xl font-bold mb-2">{step.title}</div>
              <p className="text-green-100">{step.description}</p>
              <div className="w-12 h-12 rounded-full bg-amber-400 text-green-800 flex items-center justify-center font-bold text-xl mt-4">
                {step.step}
              </div>
              
              {index < 2 && (
                <motion.div 
                  animate={{ x: [0, 10, 0] }} 
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="hidden md:block absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2">
                  <ArrowRight size={24} className="text-amber-300" />
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
        
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="mt-12 text-center"
        >
          <button className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-white font-bold rounded-full hover:from-amber-500 hover:to-amber-600 transition-colors shadow-lg">
            Apply Now
          </button>
        </motion.div>
      </motion.section>

    </div>
  );
};

export default Programs;